import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { PBPUTILS, UTILS } from "../../config/API";
import jsPDF from 'jspdf';
import SuccessPage from '../SuccessPage';
import { withSanitizedFileName } from "../../utils/sanitizeFileName";

const NewBusinessLicenseApplication = () => {
  const [formData, setFormData] = useState({
    businessName: '',
    businessAddress: '',
    ownerFirstName: '',
    ownerMiddleName: '',
    ownerLastName: '',
    ownerDateOfBirth: '',
    ownerAddress: '',
    ownerCity: '',
    ownerState: '',
    ownerZipCode: '',
    businessTelephone: '',
    homeTelephone: '',
    contactEmail: '',
    ssnFein: '',
    typeOfProductService: [], // Changed from businessType
    businessDescription: '',
    foodHandlerExpDate: '',
    isNewBusiness: '',
    startDate: '',
    multipleLocations: '',
    agreeToTerms: false, // This will now be the new mandatory checkbox
    agreeToReports: false,
    printedName: '',
    title: '',
    businessCategory: '',
    natureOfBusiness: [],
    // Liquor-specific fields
    liquorCompliance: '',
    abcLicenseNumber: '',
    abcExpirationDate: '',
    // Fuel-specific fields
    transportOwnProduct: '',
    liquidFuelsCarrierNumber: '',
    transporterName: '',
    applicantAge18: '',
    // Tobacco-specific fields
    tobaccoStartDate: '',
    acquiredExistingBusiness: '',
    previousOwnerName: '',
    // Tobacco affirmative questions
    tobaccoConvictedFraud: '',
    tobaccoConvictedFraudExplanation: '',
    tobaccoConvictedFelony: '',
    tobaccoConvictedFelonyExplanation: '',
    tobaccoLicenseRevoked: '',
    tobaccoLicenseRevokedExplanation: '',
    tobaccoTaxLiabilities: '',
    tobaccoTaxLiabilitiesExplanation: '',
    // Tobacco agreements
    tobaccoAgreeWholesaler: false,
    tobaccoAgreePayment: false,
    tobaccoAgreeReports: false,
    tobaccoAgreeComply: false,
    tobaccoAgreeSecurity: false,
    tobaccoAgreeNonTransferable: false,
    // Fuel certifications
    fuelAgreeOwnershipChange: false,
    fuelAgreeMotorFuelReport: false,
  });

  // File upload states
  const [foodHandlerPermitFile, setFoodHandlerPermitFile] = useState(null);
  const [tribalMemberIdFile, setTribalMemberIdFile] = useState(null);

  // Signature pad state and ref
  const sigCanvas = useRef(null);
  const [isSigned, setIsSigned] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [currentDate] = useState(new Date().toLocaleDateString());
  const [signatureDataURL, setSignatureDataURL] = useState('');
  const licenseTypeOptions = [

    { value: 'annual', label: 'Annual Business License: $50.00' },

  ];
  // State options
  const stateOptions = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE',
    'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY',
    'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS',
    'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM',
    'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA',
    'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT',
    'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  // Nature of Business options from screenshot - now checkboxes
  const natureOfBusinessOptions = [
    'Retail Goods',
    'Retail Services',
    'Wholesaler',
    'Manufacturer',
    'Contractor',
    'Utility',
    'Food Vendor',
    'Other'
  ];

  // Type of Product/Service options - initially empty, will be populated from API
  const [typeOfProductServiceOptions, setTypeOfProductServiceOptions] = useState([]);
  const [loadingTypeOfProductService, setLoadingTypeOfProductService] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [submissionData, setSubmissionData] = useState(null);

  // Get taxpayer ID from localStorage
  const taxpayerId = localStorage.getItem("selectedTaxPayer") || '109';

  // Fetch type of product/service from API
  useEffect(() => {
    const fetchTypeOfProductService = async () => {
      try {
        setLoadingTypeOfProductService(true);
        const apiUrl = UTILS + `?action=idv&s=businessLicenseType`;

        console.log('Fetching type of product/service from:', apiUrl);

        const response = await fetch(apiUrl);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('Type of product/service API response:', data);

        // Handle the API response format: [{ "valuetext" : "General" }, { "valuetext" : "Tobacco" }, ...]
        if (Array.isArray(data)) {
          // Map the valuetext to the format needed for our component
          const formattedOptions = data.map(item => ({
            sval: item.valuetext.replace(/\s+/g, '-'),
            dval: item.valuetext // Use the valuetext as display text
          }));
          console.log('Formatted type of product/service:', formattedOptions);
          setTypeOfProductServiceOptions(formattedOptions);
        } else {
          throw new Error('Unexpected response format from type of product/service API');
        }
      } catch (error) {
        console.error('Error fetching type of product/service:', error);
        setError(error.message);
        // Fallback to default options if API fails
        setTypeOfProductServiceOptions([
          { sval: 'general', dval: 'General' },
          { sval: 'tobacco', dval: 'Tobacco' },
          { sval: 'alcohol', dval: 'Alcohol' },
          { sval: 'fuel', dval: 'Fuel' },
          { sval: 'caisno', dval: 'Caisno' }
        ]);
      } finally {
        setLoadingTypeOfProductService(false);
      }
    };

    fetchTypeOfProductService();
  }, []);

  // Ensure the 'General' type is selected by default and cannot be removed
  useEffect(() => {
    if (typeOfProductServiceOptions && typeOfProductServiceOptions.length > 0) {
      const generalOption = typeOfProductServiceOptions.find(opt => opt.dval && opt.dval.toLowerCase().trim() === 'general');
      if (generalOption) {
        const generalSval = generalOption.sval;
        setFormData(prev => {
          const current = Array.isArray(prev.typeOfProductService) ? prev.typeOfProductService : [];
          if (!current.includes(generalSval)) {
            return { ...prev, typeOfProductService: [generalSval, ...current] };
          }
          return prev;
        });
      }
    }
  }, [typeOfProductServiceOptions]);

  // Initialize canvas size after component mounts
  useEffect(() => {
    const updateCanvasSize = () => {
      const container = document.getElementById('signature-container');
      if (container) {
        const width = container.clientWidth - 32;
        const height = 150;
        setCanvasSize({ width, height });

        if (sigCanvas.current) {
          const isEmpty = sigCanvas.current.isEmpty();
          if (!isEmpty) {
            const data = sigCanvas.current.toData();
            sigCanvas.current.clear();
            setTimeout(() => {
              if (sigCanvas.current) {
                sigCanvas.current.fromData(data);
              }
            }, 0);
          }
        }
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);

    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, '');

    // Limit to 10 digits
    const limited = cleaned.slice(0, 10);

    // Format based on length
    if (limited.length <= 3) {
      return limited;
    } else if (limited.length <= 6) {
      return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
    } else {
      return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
    }
  };

  // File input change handler
  const handleFileChange = (e, which) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    if (which === 'food') {
      setFoodHandlerPermitFile(file);
    } else if (which === 'tribal') {
      setTribalMemberIdFile(file);
    }
  };

  // Handle phone number input change
  const handlePhoneNumberChange = (e) => {
    const { name, value } = e.target;
    const formatted = formatPhoneNumber(value);

    setFormData(prev => ({
      ...prev,
      [name]: formatted
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox' && name === 'typeOfProductService') {
      // Handle type of product/service checkboxes
      setFormData(prev => {
        const currentTypes = prev.typeOfProductService || [];
        if (checked) {
          // Add the value if checked
          return {
            ...prev,
            typeOfProductService: [...currentTypes, value]
          };
        } else {
          // Prevent removing the 'General' type (use dval to find actual sval)
          const generalOption = typeOfProductServiceOptions.find(opt => opt.dval && opt.dval.toLowerCase().trim() === 'general');
          const generalSval = generalOption ? generalOption.sval : null;
          if (generalSval && value === generalSval) {
            return prev;
          }
          // Remove the value if unchecked
          return {
            ...prev,
            typeOfProductService: currentTypes.filter(type => type !== value)
          };
        }
      });
    } else if (type === 'checkbox' && name === 'natureOfBusiness') {
      // Handle nature of business checkboxes
      setFormData(prev => {
        const currentTypes = prev.natureOfBusiness || [];
        if (checked) {
          // Add the value if checked
          return {
            ...prev,
            natureOfBusiness: [...currentTypes, value]
          };
        } else {
          // Remove the value if unchecked
          return {
            ...prev,
            natureOfBusiness: currentTypes.filter(type => type !== value)
          };
        }
      });
    } else {
      // Handle all other inputs
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // Signature functions
  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setIsSigned(false);
      setSignatureDataURL('');
    }
  };

  const handleSignatureBegin = () => {
    setIsSigned(true);
  };

  const handleSignatureEnd = () => {
    // Signature completion logic - ensure signature is marked as complete
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      setIsSigned(true);
      const sigDataURL = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
      setSignatureDataURL(sigDataURL);
    } else {
      setIsSigned(false);
      setSignatureDataURL('');
    }
  };

  // Helper function to convert base64 data URL to blob
  const dataURLtoBlob = (dataurl) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  // Helper function to convert PDF to Blob
  const pdfToBlob = (pdf) => {
    const pdfOutput = pdf.output('blob');
    return pdfOutput;
  };

  // Generate PDF function with detailed field information - accepts optional signature override (use when generating for send, since setState is async)
  const generatePDF = (signatureDataURLOverride) => {
    const pdf = new jsPDF();
    const signatureToUse = signatureDataURLOverride !== undefined ? signatureDataURLOverride : signatureDataURL;
    const formatAmount = (value) => {
      if (value === null || value === undefined) return '0';
      const s = String(value).replace(/,/g, '').trim();
      if (s === '') return '0';
      const num = Number(s);
      if (isNaN(num)) return String(value);
      if (s.includes('.')) {
        const decimals = s.split('.')[1].length;
        return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
      }
      return num.toLocaleString();
    };
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    let yPosition = margin;

    // Set font and colors
    pdf.setFont('helvetica');
    pdf.setTextColor(0, 0, 0); // #840028

    // Header Section
    pdf.setFontSize(13);
    pdf.text('PRAIRIE BAND POTAWATOMI TAX COMMISSION', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 6;

    pdf.setFontSize(9);
    pdf.text('16281 Q Rd.', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 4;
    pdf.text('Mayetta, KS 66509', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 4;
    pdf.text('Telephone: (785) 966-3999', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 7;

    // Application Title
    pdf.setFontSize(11);
    pdf.setTextColor(0, 0, 0);
    pdf.text('APPLICATION FOR TRIBAL BUSINESS LICENSE', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 9;

    // Instructions Section
    pdf.setFontSize(8);
    pdf.setTextColor(0, 0, 0);

    const instructionsText = 'Instructions: All information must be completed prior to license issuance. This application must be accompanied by the appropriate non-refundable application fee:';
    const instructionLines = pdf.splitTextToSize(instructionsText, pageWidth - (margin * 2));

    instructionLines.forEach(line => {
      pdf.text(line, margin, yPosition);
      yPosition += 4;
    });

    // Fee Information - calculate total from selected product/service types
    yPosition += 2;
    const feeMap = { general: 50, liquor: 530, alcohol: 530, fuel: 100, tobacco: 100, casino: 0, caisno: 0 };
    const types = formData.typeOfProductService || [];
    const totalFee = types.reduce((sum, type) => sum + (feeMap[String(type).toLowerCase()] ?? 0), 0);
    const feeText = `Total Amount Due: $${totalFee}.00`;
    pdf.text(feeText, margin, yPosition);
    yPosition += 8;

    // Business Information Section
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text('BUSINESS INFORMATION', margin, yPosition);
    yPosition += 6;

    pdf.setFontSize(8);
    pdf.setTextColor(0, 0, 0);

    // Helper: only show fields that user has filled
    const hasValue = (v) => {
      if (v === undefined || v === null) return false;
      if (typeof v === 'string' && v.trim() === '') return false;
      if (Array.isArray(v) && v.length === 0) return false;
      if (Array.isArray(v) && v.join('').trim() === '') return false;
      return true;
    };

    // Function to add text with optimized spacing (only call when value exists)
    const addCompactText = (label, value, startY, maxWidth) => {
      let currentY = startY;
      pdf.text(label, margin, currentY);
      if (value) {
        const valueLines = pdf.splitTextToSize(String(value), maxWidth);
        valueLines.forEach((line) => {
          pdf.text(line, margin + 35, currentY);
          currentY += 4;
        });
        return currentY;
      }
      pdf.text('Not provided', margin + 35, currentY);
      return currentY + 4;
    };

    // Business info - optimized layout (only fields with values)
    const businessFields = [
      { label: 'Business Name:', value: formData.businessName, width: 115 },
      { label: 'Business Address:', value: formData.businessAddress, width: 115 },
      { label: 'Business Tel:', value: formData.businessTelephone, width: 115 },
      { label: 'Home Tel:', value: formData.homeTelephone, width: 115 },
      { label: 'Email:', value: formData.contactEmail, width: 115 },
      { label: 'Description:', value: formData.businessDescription, width: 115 },
      { label: 'Category:', value: formData.businessCategory, width: 115 },
      { label: 'Nature of Business:', value: Array.isArray(formData.natureOfBusiness) ? formData.natureOfBusiness.join(', ') : formData.natureOfBusiness, width: 115 },
      { label: 'Product/Service:', value: Array.isArray(formData.typeOfProductService) ? formData.typeOfProductService.join(', ') : formData.typeOfProductService, width: 115 },
      { label: 'New Business:', value: formData.isNewBusiness, width: 115 },
      { label: 'Start Date:', value: formData.startDate, width: 115 },
      { label: 'Multi Locations:', value: formData.multipleLocations, width: 115 }
    ];

    businessFields.filter(f => hasValue(f.value)).forEach(field => {
      if (yPosition > pageHeight - 40) return;
      yPosition = addCompactText(field.label, field.value, yPosition, field.width);
      yPosition += 2;
    });

    yPosition += 3;

    // Owner Information Section
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text('OWNER INFORMATION', margin, yPosition);
    yPosition += 6;

    pdf.setFontSize(8);
    pdf.setTextColor(0, 0, 0);

    const ownerFields = [
      { label: 'First Name:', value: formData.ownerFirstName, width: 110 },
      { label: 'Middle Name:', value: formData.ownerMiddleName, width: 110 },
      { label: 'Last Name:', value: formData.ownerLastName, width: 110 },
      { label: 'Date of Birth:', value: formData.ownerDateOfBirth, width: 110 },
      { label: 'Address:', value: formData.ownerAddress, width: 110 },
      { label: 'City:', value: formData.ownerCity, width: 110 },
      { label: 'State:', value: formData.ownerState, width: 110 },
      { label: 'Zip Code:', value: formData.ownerZipCode, width: 110 },
      { label: 'SSN/FEIN:', value: formData.ssnFein, width: 110 }
    ];

    ownerFields.filter(f => hasValue(f.value)).forEach(field => {
      if (yPosition > pageHeight - 60) return;
      yPosition = addCompactText(field.label, field.value, yPosition, field.width);
      yPosition += 2;
    });

    yPosition += 3;

    // Food Vendor Notice - Moved under Owner Information
    if (yPosition < pageHeight - 45) {
      pdf.setFontSize(8);
      pdf.setTextColor(0, 0, 0);
      pdf.text('FOOD VENDOR NOTICE:', margin, yPosition);
      yPosition += 5;

      pdf.setFontSize(7);
      pdf.setTextColor(0, 0, 0);

      const foodVendorText = 'Effective 7/1/2017: Persons that produce or sell food sold directly to the end consumer must submit proof of training within the past 2 years in basic food handling principles accredited by ANSI or ServSafe.. Online: https://www.ihs.gov/foodhandler';
      const foodVendorLines = pdf.splitTextToSize(foodVendorText, pageWidth - (margin * 2));

      foodVendorLines.forEach(line => {
        if (yPosition > pageHeight - 35) return;
        pdf.text(line, margin, yPosition);
        yPosition += 3.5;
      });

      yPosition += 2;
      if (formData.foodHandlerExpDate && yPosition < pageHeight - 30) {
        yPosition = addCompactText('Food Handler Exp:', formData.foodHandlerExpDate, yPosition, 115);
        yPosition += 2;
      }
      yPosition += 4;
    }

    const hasFuel = (formData.typeOfProductService || []).some(t => String(t).toLowerCase() === 'fuel');
    const hasLiquor = (formData.typeOfProductService || []).some(t => ['liquor', 'alcohol'].includes(String(t).toLowerCase()));
    const hasTobacco = (formData.typeOfProductService || []).some(t => String(t).toLowerCase() === 'tobacco');

    const checkPageBreak = (neededSpace = 30) => {
      if (yPosition > pageHeight - neededSpace) {
        pdf.addPage();
        yPosition = margin;
      }
    };

    // Fuel-specific section (only if at least one fuel field has value)
    const fuelFields = [
      { label: 'Transport Own Product:', value: formData.transportOwnProduct, width: 110 },
      { label: 'Liquid Fuels Carrier #:', value: formData.liquidFuelsCarrierNumber, width: 110 },
      { label: 'Transporter Name:', value: formData.transporterName, width: 110 },
      { label: 'Applicant 18+:', value: formData.applicantAge18, width: 110 },
      { label: 'Agree Ownership Change:', value: formData.fuelAgreeOwnershipChange ? 'Yes' : null, width: 110 },
      { label: 'Agree Motor Fuel Report:', value: formData.fuelAgreeMotorFuelReport ? 'Yes' : null, width: 110 }
    ].filter(f => hasValue(f.value));
    if (hasFuel && fuelFields.length > 0) {
      checkPageBreak(80);
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.text('FUEL BUSINESS INFORMATION', margin, yPosition);
      yPosition += 6;
      pdf.setFontSize(8);
      fuelFields.forEach(field => {
        if (yPosition > pageHeight - 40) return;
        yPosition = addCompactText(field.label, field.value, yPosition, field.width);
        yPosition += 2;
      });
      yPosition += 3;
    }

    // Liquor-specific section (only if at least one liquor field has value)
    const liquorFields = [
      { label: 'Liquor Compliance:', value: formData.liquorCompliance, width: 110 },
      { label: 'ABC License #:', value: formData.abcLicenseNumber, width: 110 },
      { label: 'ABC Exp. Date:', value: formData.abcExpirationDate, width: 110 }
    ].filter(f => hasValue(f.value));
    if (hasLiquor && liquorFields.length > 0) {
      checkPageBreak(60);
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.text('LIQUOR LICENSE INFORMATION', margin, yPosition);
      yPosition += 6;
      pdf.setFontSize(8);
      liquorFields.forEach(field => {
        if (yPosition > pageHeight - 40) return;
        yPosition = addCompactText(field.label, field.value, yPosition, field.width);
        yPosition += 2;
      });
      yPosition += 3;
    }

    // Tobacco-specific section (only if at least one tobacco field has value)
    const tobaccoFields = [
      { label: 'Tobacco Start Date:', value: formData.tobaccoStartDate, width: 110 },
      { label: 'Acquired Existing:', value: formData.acquiredExistingBusiness, width: 110 },
      { label: 'Previous Owner:', value: formData.previousOwnerName, width: 110 },
      { label: 'Convicted Fraud:', value: formData.tobaccoConvictedFraud, width: 110 },
      { label: 'Fraud Explanation:', value: formData.tobaccoConvictedFraudExplanation, width: 110 },
      { label: 'Convicted Felony:', value: formData.tobaccoConvictedFelony, width: 110 },
      { label: 'Felony Explanation:', value: formData.tobaccoConvictedFelonyExplanation, width: 110 },
      { label: 'License Revoked:', value: formData.tobaccoLicenseRevoked, width: 110 },
      { label: 'Revoked Explanation:', value: formData.tobaccoLicenseRevokedExplanation, width: 110 },
      { label: 'Tax Liabilities:', value: formData.tobaccoTaxLiabilities, width: 110 },
      { label: 'Liabilities Explanation:', value: formData.tobaccoTaxLiabilitiesExplanation, width: 110 },
      { label: 'Agree Wholesaler:', value: formData.tobaccoAgreeWholesaler ? 'Yes' : null, width: 110 },
      { label: 'Agree Payment:', value: formData.tobaccoAgreePayment ? 'Yes' : null, width: 110 },
      { label: 'Agree Reports:', value: formData.tobaccoAgreeReports ? 'Yes' : null, width: 110 },
      { label: 'Agree Comply:', value: formData.tobaccoAgreeComply ? 'Yes' : null, width: 110 },
      { label: 'Agree Security:', value: formData.tobaccoAgreeSecurity ? 'Yes' : null, width: 110 },
      { label: 'Agree Non-Transferable:', value: formData.tobaccoAgreeNonTransferable ? 'Yes' : null, width: 110 }
    ].filter(f => hasValue(f.value));
    if (hasTobacco && tobaccoFields.length > 0) {
      checkPageBreak(120);
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.text('TOBACCO BUSINESS INFORMATION', margin, yPosition);
      yPosition += 6;
      pdf.setFontSize(8);
      tobaccoFields.forEach(field => {
        if (yPosition > pageHeight - 40) return;
        yPosition = addCompactText(field.label, field.value, yPosition, field.width);
        yPosition += 2;
      });
      yPosition += 3;
    }

    // Certification Section
    if (yPosition < pageHeight - 50) {
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Certification', margin, yPosition);
      yPosition += 6;

      pdf.setFontSize(7);
      pdf.setTextColor(0, 0, 0);

      const certificationText = 'I certify that I am a duly authorized owner, partner, director, officer, or representative of the applicant and that the statements made herein are true and correct to the best of my knowledge. I further agree on behalf of the applicant to be bound by and comply with all laws of the Prairie Band Potawatomi Nation and do hereby consensually submit to the jurisdiction of the Prairie Band Potawatomi Nation.';

      const certLines = pdf.splitTextToSize(certificationText, pageWidth - (margin * 2));
      certLines.forEach(line => {
        if (yPosition > pageHeight - 30) return;
        pdf.text(line, margin, yPosition);
        yPosition += 3.5;
      });

      yPosition += 3;

      // Agreements
      if (formData.agreeToTerms && yPosition < pageHeight - 25) {
        pdf.text('I agree to the above terms and conditions.', margin, yPosition);
        yPosition += 4;
      }

      if (formData.agreeToReports && yPosition < pageHeight - 25) {
        pdf.text('I agree to submit regular Sales Revenue reports.', margin, yPosition);
        yPosition += 4;
      }
    }

    // Signature Section
    if (yPosition < pageHeight - 35) {
      // Signature image if available
      if (signatureToUse && yPosition < pageHeight - 30) {
        try {
          const signatureWidth = 55;
          const signatureHeight = 18;
          pdf.text('Signature:', margin, yPosition);
          yPosition += 4;
          pdf.addImage(signatureToUse, 'PNG', margin, yPosition, signatureWidth, signatureHeight);
          yPosition += signatureHeight + 4;
        } catch (error) {
          pdf.text('Signature: Provided', margin, yPosition);
          yPosition += 4;
        }
      } else {
        pdf.text('Signature: Provided', margin, yPosition);
        yPosition += 4;
      }

      // Final details
      const details = [];
      if (formData.printedName) details.push(`Printed Name: ${formData.printedName}`);
      if (formData.title) details.push(`Title: ${formData.title}`);
      details.push(`Date Signed: ${currentDate}`);

      const detailsText = details.join(' | ');
      const detailLines = pdf.splitTextToSize(detailsText, pageWidth - (margin * 2));
      detailLines.forEach(line => {
        if (yPosition > pageHeight - 15) return;
        pdf.text(line, margin, yPosition);
        yPosition += 4;
      });
    }

    // Payment notice at bottom
    if (yPosition < pageHeight - 10) {
      pdf.setFontSize(7);
      pdf.setTextColor(0, 0, 0);
      const paymentText = '*Non-refundable Application Fee must be submitted with application. Make checks payable to Prairie Band Potawatomi Tax Commission.';
      const paymentLines = pdf.splitTextToSize(paymentText, pageWidth - (margin * 2));
      paymentLines.forEach((line, index) => {
        pdf.text(line, margin, pageHeight - 10 + (index * 3));
      });
    }

    return pdf;
  };


  // Function to handle PDF download from SuccessPage
  const handleDownloadPDF = () => {
    const pdf = generatePDF();
    const fileName = `BusinessLicenseApplication_${formData.businessName || 'application'}_${currentDate.replace(/\//g, '-')}.pdf`;
    pdf.save(fileName);
  };

  // Function to handle back to form
  const handleBackToForm = () => {
    setShowSuccessScreen(false);
    // Reset form if needed
  };

  // API submission function with PDF
  const submitFormData = async (submissionData) => {
    try {
      setSubmitting(true);

      // Generate PDF with signature passed in (state update is async, so state would still be old here)
      const pdf = generatePDF(submissionData.signature);
      const pdfBlob = pdfToBlob(pdf);
      const pdfFile = new File([pdfBlob], `BusinessLicenseApplication_${submissionData.businessName || 'application'}.pdf`, {
        type: 'application/pdf'
      });

      // Format date for API (convert from YYYY-MM-DD to MM/DD/YYYY)
      const formatDateForAPI = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
      };

      // Calculate total fee based on selected product/service types
      const calculateTotalFee = (types) => {
        if (!Array.isArray(types) || types.length === 0) return 0;
        const feeMap = {
          general: 50,
          liquor: 530,
          alcohol: 530,
          fuel: 100,
          tobacco: 100,
          casino: 0,
          caisno: 0
        };
        return types.reduce((sum, type) => {
          const key = String(type).toLowerCase();
          return sum + (feeMap[key] !== undefined ? feeMap[key] : 0);
        }, 0);
      };

      const totalFee = calculateTotalFee(submissionData.typeOfProductService);

      // Prepare business license data
      const businessLicenseData = {
        isnew: 'true',
        businessname: submissionData.businessName || '',
        businessaddress: submissionData.businessAddress || '',
        ownerfname: submissionData.ownerFirstName || '',
        ownermname: submissionData.ownerMiddleName || '',
        ownerlname: submissionData.ownerLastName || '',
        dateofbirth: formatDateForAPI(submissionData.ownerDateOfBirth) || '',
        owneraddress: submissionData.ownerAddress || '',
        ownercity: submissionData.ownerCity || '',
        ownerstate: submissionData.ownerState || '',
        ownerzip: submissionData.ownerZipCode || '',
        businesstele: submissionData.businessTelephone || '',
        homephone: submissionData.homeTelephone || '',
        contactemail: submissionData.contactEmail || '',
        last4ssn: submissionData.ssnFein || '',
        type: Array.isArray(submissionData.typeOfProductService) ? submissionData.typeOfProductService.join(',') : submissionData.typeOfProductService || '',
        totalfee: totalFee,
        businessdesc: submissionData.businessDescription || '',
        datebegan: formatDateForAPI(submissionData.startDate) || '',
        businesscategory: submissionData.businessCategory || '',
        natureofbusiness: Array.isArray(submissionData.natureOfBusiness) ? submissionData.natureOfBusiness.join(',') : submissionData.natureOfBusiness || '',
        moreThanOne: submissionData.multipleLocations || '',
        taxtype: submissionData.businessCategory || '',
        foodhandlerexpdate: formatDateForAPI(submissionData.foodHandlerExpDate) || '',
        printedName: submissionData.printedName || '',
        title: submissionData.title || '',
        // Fuel-specific fields
        transportOwnProduct: submissionData.transportOwnProduct || '',
        liquidFuelsCarrierNumber: submissionData.liquidFuelsCarrierNumber || '',
        transporterName: submissionData.transporterName || '',
        applicantAge18: submissionData.applicantAge18 || '',
        fuelAgreeOwnershipChange: submissionData.fuelAgreeOwnershipChange ? 'Yes' : 'No',
        fuelAgreeMotorFuelReport: submissionData.fuelAgreeMotorFuelReport ? 'Yes' : 'No',
        // Liquor-specific fields
        liquorCompliance: submissionData.liquorCompliance || '',
        abcLicenseNumber: submissionData.abcLicenseNumber || '',
        abcExpirationDate: submissionData.abcExpirationDate || '',
        // Tobacco-specific fields
        tobaccoStartDate: submissionData.tobaccoStartDate || '',
        acquiredExistingBusiness: submissionData.acquiredExistingBusiness || '',
        previousOwnerName: submissionData.previousOwnerName || '',
        tobaccoConvictedFraud: submissionData.tobaccoConvictedFraud || '',
        tobaccoConvictedFraudExplanation: submissionData.tobaccoConvictedFraudExplanation || '',
        tobaccoConvictedFelony: submissionData.tobaccoConvictedFelony || '',
        tobaccoConvictedFelonyExplanation: submissionData.tobaccoConvictedFelonyExplanation || '',
        tobaccoLicenseRevoked: submissionData.tobaccoLicenseRevoked || '',
        tobaccoLicenseRevokedExplanation: submissionData.tobaccoLicenseRevokedExplanation || '',
        tobaccoTaxLiabilities: submissionData.tobaccoTaxLiabilities || '',
        tobaccoTaxLiabilitiesExplanation: submissionData.tobaccoTaxLiabilitiesExplanation || '',
        tobaccoAgreeWholesaler: submissionData.tobaccoAgreeWholesaler ? 'Yes' : 'No',
        tobaccoAgreePayment: submissionData.tobaccoAgreePayment ? 'Yes' : 'No',
        tobaccoAgreeReports: submissionData.tobaccoAgreeReports ? 'Yes' : 'No',
        tobaccoAgreeComply: submissionData.tobaccoAgreeComply ? 'Yes' : 'No',
        tobaccoAgreeSecurity: submissionData.tobaccoAgreeSecurity ? 'Yes' : 'No',
        tobaccoAgreeNonTransferable: submissionData.tobaccoAgreeNonTransferable ? 'Yes' : 'No',
        agreeToReports: submissionData.agreeToReports ? 'Yes' : 'No',
        agreeToTerms: submissionData.agreeToTerms ? 'Yes' : 'No'
      };

      // Store submission data for SuccessPage
      setSubmissionData({
        formData: submissionData,
        businessLicenseData: businessLicenseData
      });

      // Create FormData object
      const formDataToSend = new FormData();

      // Add the business license data as JSON string
      formDataToSend.append('Body', JSON.stringify(businessLicenseData));
      console.log('Business License Data being sent:', businessLicenseData);

      // Add signature as blob
      if (submissionData.signature) {
        const signatureBlob = dataURLtoBlob(submissionData.signature);
        formDataToSend.append('Signature', signatureBlob, 'signature.png');
      }

      // Add PDF to FormData (name includes user-entered business name)
      formDataToSend.append('PDF', withSanitizedFileName(pdfFile));

      // Add uploaded files if present
      if (submissionData.foodHandlerPermitFile) {
        const safePermitFile = withSanitizedFileName(submissionData.foodHandlerPermitFile);
        formDataToSend.append('foodHandlerPermit', safePermitFile, safePermitFile.name);
      }

      if (submissionData.tribalMemberIdFile) {
        const safeTribalIdFile = withSanitizedFileName(submissionData.tribalMemberIdFile);
        formDataToSend.append('tribalMemberID', safeTribalIdFile, safeTribalIdFile.name);
      }

      // Add printed name and title
      formDataToSend.append('printedName', submissionData.printedName);
      formDataToSend.append('title', submissionData.title);

      // Construct API URL
      const apiUrl = PBPUTILS + `?reporttype=buslicense`;

      console.log('Submitting to:', apiUrl);
      console.log('Business license data being sent:', businessLicenseData);
      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formDataToSend,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.text();
      console.log('API Response:', responseData);

      // Handle the specific success response
      if (responseData.includes('{"Status":"Success","Message":"Business License Created / Updated"}')) {
        setShowSuccessScreen(true);
        return {
          success: true,
          message: 'Business license application submitted successfully!'
        };
      }

      // Handle different API responses
      if (responseData.includes('Record already exist for the report type of BUSINESSLICENSE')) {
        const match = responseData.match(/Record already exist for the report type of BUSINESSLICENSE in tax return date (\d+) \/ (\d+) for taxpayer (.+)/);
        if (match) {
          const [, month, year, taxpayerName] = match;
          return {
            success: false,
            message: `A business license application already exists for ${month}/${year} for taxpayer ${taxpayerName}. Please contact support if you need to update an existing application.`,
            duplicateRecord: true
          };
        } else {
          return {
            success: false,
            message: 'A business license application already exists. Please contact support if you need to update an existing application.',
            duplicateRecord: true
          };
        }
      } else if (responseData.trim().toLowerCase() === 'true' || responseData.toLowerCase().includes('success')) {
        setShowSuccessScreen(true);
        return {
          success: true,
          message: 'Business license application submitted successfully!'
        };
      } else {
        return {
          success: false,
          message: `Unexpected response from server: ${responseData}`
        };
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      return {
        success: false,
        message: `Failed to submit application: ${error.message}`
      };
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isSigned || (sigCanvas.current && sigCanvas.current.isEmpty())) {
      alert('Please provide a signature before submitting the application.');
      return;
    }

    // Validate that at least one type of product/service is selected
    if (!formData.typeOfProductService || formData.typeOfProductService.length === 0) {
      alert('Please select at least one type of product/service.');
      return;
    }

    // Validate that at least one nature of business is selected
    if (!formData.natureOfBusiness || formData.natureOfBusiness.length === 0) {
      alert('Please select at least one Nature of Business.');
      return;
    }

    // Validate business category is selected
    if (!formData.businessCategory) {
      alert('Please select a business category (Retailer or Distributor).');
      return;
    }

    // Validate printed name and title
    if (!formData.printedName.trim()) {
      alert('Please enter your printed name.');
      return;
    }

    if (!formData.title.trim()) {
      alert('Please enter your title.');
      return;
    }

    const signatureData = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
    setSignatureDataURL(signatureData);

    const submissionData = {
      ...formData,
      signature: signatureData,
      signatureDate: currentDate
    };
    // Attach selected files to submissionData so submitFormData can append them
    if (foodHandlerPermitFile) submissionData.foodHandlerPermitFile = foodHandlerPermitFile;
    if (tribalMemberIdFile) submissionData.tribalMemberIdFile = tribalMemberIdFile;

    console.log('Form data to submit:', submissionData);

    // Call the API submission function
    const result = await submitFormData(submissionData);

    if (!result.success && !result.duplicateRecord) {
      alert(`Submission failed: ${result.message}`);
    }
  };

  // Prepare report details for SuccessPage
  const reportDetails = {
    'Application Type': 'Business License Application',
    'Business Name': formData.businessName,
    'Business Category': formData.businessCategory,
    'Nature of Business': Array.isArray(formData.natureOfBusiness) ? formData.natureOfBusiness.join(', ') : formData.natureOfBusiness,
    'Submitted Date': currentDate,
    'Type of Product/Service': Array.isArray(formData.typeOfProductService) ? formData.typeOfProductService.join(', ') : formData.typeOfProductService
  };

  // If showing success screen, render the SuccessPage component
  if (showSuccessScreen) {
    return (
      <SuccessPage
        title="Business License Application Submitted Successfully!"
        subtitle={`Your business license application for ${formData.businessName} has been successfully submitted.`}
        downloadButtonText="Download Business License Application PDF"
        backButtonText="Back to Home"
        onDownload={handleDownloadPDF}
        onBack={handleBackToForm}
        reportDetails={reportDetails}
        contactInfo={{
          phone: "(785) 966-3060",
          email: "taxcommission@pbpnation.org",
          department: "Prairie Band Potawatomi Tax Commission"
        }}
      >
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="font-semibold text-green-800 mb-2">Next Steps:</h3>
          <ul className="text-sm text-green-700 space-y-1">
            <li>• Your application is now under review</li>
            <li>• You will be contacted if additional information is needed</li>
            <li>• Keep this PDF for your records</li>
            <li>• Contact us if you have any questions about your application status</li>
          </ul>
        </div>
      </SuccessPage>
    );
  }

  return (
    <div className="min-h-screen bg-[#840028] py-8 px-4 overflow-x-hidden">
      <div className="max-w-4xl mx-auto bg-white shadow-2xl rounded-2xl border border-gray-100 overflow-hidden">
        {/* Header Section - Updated to match image */}
        <div className="bg-white text-center mb-6 pb-6 border-b-2 border-[#840028] pt-8">
          <div className="mb-4">
            <div className="text-2xl font-bold text-[#840028] uppercase tracking-wide">
              PRAIRIE BAND POTAWATOMI TAX COMMISSION
            </div>
            <div className="text-base text-gray-700 mt-2">
              16281 Q Rd.
            </div>
            <div className="text-base text-gray-700">
              Mayetta, KS 66509
            </div>
            <div className="text-base text-gray-700 mt-1">
              Telephone: (785) 966-3999
            </div>
            <div className="text-base text-gray-700">
              taxcommission@pbpnation.org
            </div>
            <h1 className="text-lg font-bold text-[#840028] mt-4 mb-4 uppercase tracking-wide">
              APPLICATION FOR TRIBAL BUSINESS LICENSE
            </h1>

            {/* Type of Product/Service Section */}
            <div className="mt-6 mx-4 sm:mx-8 text-left">
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <label className="block font-semibold text-gray-700 mb-2">
                  Type of Product/Service <span className="text-red-500">*</span>
                  {formData.typeOfProductService && formData.typeOfProductService.length > 0 && (
                    <span className="text-sm text-green-600 font-normal ml-2">
                      ({formData.typeOfProductService.length} selected)
                    </span>
                  )}
                </label>
                {loadingTypeOfProductService ? (
                  <div className="flex items-center justify-center p-8 border border-gray-200 rounded-lg">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#840028]"></div>
                    <span className="ml-3 text-gray-600">Loading types of product/service...</span>
                  </div>
                ) : error ? (
                  <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                    <p className="text-red-700">Error loading types of product/service: {error}</p>
                    <p className="text-red-600 text-sm mt-2">Using default options</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {typeOfProductServiceOptions.map(type => {
                      const isGeneral = type.dval && type.dval.toLowerCase().trim() === 'general';
                      return (
                        <label
                          key={type.sval}
                          className={`flex items-center space-x-3 p-3 border border-gray-200 rounded-lg ${
                            isGeneral ? 'bg-gray-50 cursor-default opacity-95' : 'hover:bg-gray-50 cursor-pointer'
                          } transition-colors duration-200`}
                        >
                          <input
                            type="checkbox"
                            name="typeOfProductService"
                            value={type.sval}
                            checked={formData.typeOfProductService?.includes(type.sval) || false}
                            onChange={isGeneral ? undefined : handleInputChange}
                            disabled={isGeneral}
                            className="text-[#840028] focus:ring-[#840028] rounded"
                          />
                          <span className="text-gray-700">
                            {isGeneral ? 'General (Waived for tribal citizens)' : type.dval}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>



        </div>

        {/* Instructions Section  */}

        <div className="px-8">
          {formData.typeOfProductService?.includes('Liquor') && (
            <div className="border border-gray-300 p-4 mb-6 bg-gray-50 rounded-lg">
              <h3 className="font-bold text-[#840028] mb-3">Instructions - Liquor License</h3>
              <p className="mb-3 text-sm">
                All information must be completed prior to license issuance. This application for the Prairie Band of Potawatomi Liquor License must be accompanied by the appropriate non-refundable application fee:
              </p>
              <div className="ml-4 space-y-2 text-sm">
                <div className="flex justify-between max-w-md">
                  <span>New License Fee:</span>
                  <span className="font-semibold">$30.00</span>
                </div>
               
                <div className="flex justify-between max-w-md">
                  <span>Standard Liquor License Fee:</span>
                  <span className="font-semibold">$500.00</span>
                </div>
              </div>
            </div>
          )}
          
          {formData.typeOfProductService?.includes('Fuel') && (
            <div className="border border-gray-300 p-4 mb-6 bg-gray-50 rounded-lg">
              <h3 className="font-bold text-[#840028] mb-3">Tribal Motor Fuel Distributor or Retailer License Application</h3>
              <p className="mb-3 text-sm">
                <strong>New or Renewal of Motor Fuel License Fee $100.00 (Section 10-6-10)</strong>
              </p>
              <p className="text-sm leading-relaxed">
                An approved Tribal Motor Fuel License is Valid for (1) Calendar Year from the date of issue and shall be nontransferable and non-assignable. If a tribal distributor establishes one (1) or more places of business at one (1) or more locations within the tribal jurisdiction, a separate license shall be required for each such separate location. The license shall be conspicuously posted in a public area in each such place of business.
              </p>
            </div>
          )}
          
          {formData.typeOfProductService?.includes('Tobacco') && (
            <div className="border border-gray-300 p-4 mb-6 bg-gray-50 rounded-lg">
              <h3 className="font-bold text-[#840028] mb-3">Tribal Tobacco Distributor/Wholesaler or Retailer License Application</h3>
              <p className="text-sm leading-relaxed">
                <strong>*Non-Refundable Application Fee of $100.00 must be submitted with application. Make checks payable to the Prairie Band Potawatomi Tax Commission.</strong>
              </p>
            </div>
          )}
          
          {(formData.typeOfProductService?.includes('Casino') || formData.typeOfProductService?.includes('General')) && (
            <div className="border border-gray-300 p-4 mb-6 bg-gray-50 rounded-lg">
              <h3 className="font-bold text-[#840028] mb-3">Instructions - Business License</h3>
              <p className="mb-3 text-sm">
                All information must be completed prior to license issuance. This application for the Prairie Band of Potawatomi Business License must be accompanied by the appropriate non-refundable application fee.
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                {licenseTypeOptions.map(license => (
                  <li key={license.value} className="ml-4">{license.label}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="px-4 sm:px-8">
          {/* Business Information */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8 mb-8 shadow-sm">
            <div className="flex items-center mb-6 pb-4 border-b border-gray-100">
              <div className="bg-[#840028] text-white p-2 rounded-lg mr-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800">Business Information</h3>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block font-semibold text-gray-700 mb-3">Business Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200"
                  placeholder="Enter official business name"
                  required
                />
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-3">Business Address <span className="text-red-500">*</span></label>
                <textarea
                  name="businessAddress"
                  value={formData.businessAddress}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200 resize-vertical"
                  placeholder="Full business location address"
                  required
                />
              </div>

              {/* Fuel-specific fields */}
              {formData.typeOfProductService?.includes('Fuel') && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 space-y-6 mt-6">
                  <h4 className="font-semibold text-blue-900 mb-4">Fuel Business Information</h4>
                  
                  <div>
                    <label className="block font-semibold text-gray-700 mb-3">
                      Do you transport your own product?
                    </label>
                    <div className="flex space-x-6">
                      <label className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="transportOwnProduct"
                          value="Yes"
                          checked={formData.transportOwnProduct === 'Yes'}
                          onChange={handleInputChange}
                          className="text-[#840028] focus:ring-[#840028]"
                        />
                        <span className="text-gray-700">Yes</span>
                      </label>
                      <label className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="transportOwnProduct"
                          value="No"
                          checked={formData.transportOwnProduct === 'No'}
                          onChange={handleInputChange}
                          className="text-[#840028] focus:ring-[#840028]"
                        />
                        <span className="text-gray-700">No</span>
                      </label>
                    </div>
                  </div>

                  {formData.transportOwnProduct === 'Yes' && (
                    <div>
                      <label className="block font-semibold text-gray-700 mb-3">If yes, please show your Liquid Fuels Carriers Number</label>
                      <input
                        type="text"
                        name="liquidFuelsCarrierNumber"
                        value={formData.liquidFuelsCarrierNumber}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200"
                        placeholder="Enter Liquid Fuels Carriers Number"
                      />
                    </div>
                  )}

                  {formData.transportOwnProduct === 'No' && (
                    <div>
                      <label className="block font-semibold text-gray-700 mb-3">If no, who transports this product?</label>
                      <input
                        type="text"
                        name="transporterName"
                        value={formData.transporterName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200"
                        placeholder="Enter transporter name"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block font-semibold text-gray-700 mb-3">
                      Are the applicant(s) at least 18 years of age?
                    </label>
                    <div className="flex space-x-6">
                      <label className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="applicantAge18"
                          value="Yes"
                          checked={formData.applicantAge18 === 'Yes'}
                          onChange={handleInputChange}
                          className="text-[#840028] focus:ring-[#840028]"
                        />
                        <span className="text-gray-700">Yes</span>
                      </label>
                      <label className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="applicantAge18"
                          value="No"
                          checked={formData.applicantAge18 === 'No'}
                          onChange={handleInputChange}
                          className="text-[#840028] focus:ring-[#840028]"
                        />
                        <span className="text-gray-700">No</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Owner Information */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8 mb-8 shadow-sm">
            <div className="flex items-center mb-6 pb-4 border-b border-gray-100">
              <div className="bg-[#840028] text-white p-2 rounded-lg mr-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800">Owner Information</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div>
                <label className="block font-semibold text-gray-700 mb-3">First Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="ownerFirstName"
                  value={formData.ownerFirstName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200"
                  required
                />
              </div>
              <div>
                <label className="block font-semibold text-gray-700 mb-3">Middle Name</label>
                <input
                  type="text"
                  name="ownerMiddleName"
                  value={formData.ownerMiddleName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200"
                />
              </div>
              <div>
                <label className="block font-semibold text-gray-700 mb-3">Last Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="ownerLastName"
                  value={formData.ownerLastName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200"
                  required
                />
              </div>
            </div>

            {/* Date of Birth Field */}
            <div className="mb-6">
              <label className="block font-semibold text-gray-700 mb-3">Date of Birth <span className="text-red-500">*</span></label>
              <div className="w-full overflow-hidden rounded-lg border border-gray-300 sm:overflow-visible sm:rounded-none sm:border-0">
                <input
                  type="date"
                  name="ownerDateOfBirth"
                  value={formData.ownerDateOfBirth}
                  onChange={handleInputChange}
                  className="ios-date-left w-full max-w-full min-w-0 box-border px-4 py-3 border-0 rounded-none text-left focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200 sm:border sm:border-gray-300 sm:rounded-lg"
                  style={{ maxWidth: '100%' }}
                  required
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block font-semibold text-gray-700 mb-3">Tribal Member ID (Upload)</label>
              <input
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => handleFileChange(e, 'tribal')}
                className="w-full"
              />
              {tribalMemberIdFile && (
                <p className="text-sm text-gray-600 mt-2">Selected: {tribalMemberIdFile.name}</p>
              )}
            </div>

            <div className="space-y-6">
              <div>
                <label className="block font-semibold text-gray-700 mb-3">Owner's Address <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="ownerAddress"
                  value={formData.ownerAddress}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200"
                  placeholder="Street Address"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block font-semibold text-gray-700 mb-3">City <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="ownerCity"
                    value={formData.ownerCity}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200"
                    required
                  />
                </div>
                <div>
                  <label className="block font-semibold text-gray-700 mb-3">State <span className="text-red-500">*</span></label>
                  <select
                    name="ownerState"
                    value={formData.ownerState}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200 bg-white appearance-none"
                    required
                  >
                    <option value="">Select State</option>
                    {stateOptions.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-gray-700 mb-3">Zip Code <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="ownerZipCode"
                    value={formData.ownerZipCode}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200"
                    required
                  />
                </div>
              </div>

              {formData.typeOfProductService?.includes('Tobacco') && (
                <div className="mt-8 bg-red-50 border-2 border-red-300 rounded-lg p-6">
                  <div className="mb-6 pb-4 border-b-2 border-red-300">
                    <p className="text-red-800 font-semibold mt-2">Has the applicant, its owner(s), directors, partners, or officers:</p>
                  </div>

                  <div className="space-y-6">
                    <div className="bg-white p-4 rounded-lg border border-red-200">
                      <label className="block font-semibold text-gray-800 mb-3">
                        a. Been convicted of fraud, tax evasion, or a violation of the laws governing the reporting and payment of fees or taxes for tobacco or tobacco products in the past five (5) years?
                      </label>
                      <div className="flex space-x-6 mb-3">
                        <label className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name="tobaccoConvictedFraud"
                            value="Yes"
                            checked={formData.tobaccoConvictedFraud === 'Yes'}
                            onChange={handleInputChange}
                            className="text-[#840028] focus:ring-[#840028]"
                          />
                          <span className="text-gray-700 font-medium">Yes</span>
                        </label>
                        <label className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name="tobaccoConvictedFraud"
                            value="No"
                            checked={formData.tobaccoConvictedFraud === 'No'}
                            onChange={handleInputChange}
                            className="text-[#840028] focus:ring-[#840028]"
                          />
                          <span className="text-gray-700 font-medium">No</span>
                        </label>
                      </div>
                      {formData.tobaccoConvictedFraud === 'Yes' && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">If yes, attach explanation.</label>
                          <textarea
                            name="tobaccoConvictedFraudExplanation"
                            value={formData.tobaccoConvictedFraudExplanation}
                            onChange={handleInputChange}
                            rows="2"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028]"
                            placeholder="Provide explanation..."
                          />
                        </div>
                      )}
                    </div>

                    <div className="bg-white p-4 rounded-lg border border-red-200">
                      <label className="block font-semibold text-gray-800 mb-3">
                        b. Been convicted of any felony or been granted a deferred judgment following an adjudication or guilt of a felony in the past five (5) years?
                      </label>
                      <div className="flex space-x-6 mb-3">
                        <label className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name="tobaccoConvictedFelony"
                            value="Yes"
                            checked={formData.tobaccoConvictedFelony === 'Yes'}
                            onChange={handleInputChange}
                            className="text-[#840028] focus:ring-[#840028]"
                          />
                          <span className="text-gray-700 font-medium">Yes</span>
                        </label>
                        <label className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name="tobaccoConvictedFelony"
                            value="No"
                            checked={formData.tobaccoConvictedFelony === 'No'}
                            onChange={handleInputChange}
                            className="text-[#840028] focus:ring-[#840028]"
                          />
                          <span className="text-gray-700 font-medium">No</span>
                        </label>
                      </div>
                      {formData.tobaccoConvictedFelony === 'Yes' && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">If yes, attach explanation.</label>
                          <textarea
                            name="tobaccoConvictedFelonyExplanation"
                            value={formData.tobaccoConvictedFelonyExplanation}
                            onChange={handleInputChange}
                            rows="2"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028]"
                            placeholder="Provide explanation..."
                          />
                        </div>
                      )}
                    </div>

                    <div className="bg-white p-4 rounded-lg border border-red-200">
                      <label className="block font-semibold text-gray-800 mb-3">
                        c. Formally held a tobacco wholesaler or distributor or retailers license or permit in any jurisdiction which was revoked for cause in the past five (5) years?
                      </label>
                      <div className="flex space-x-6 mb-3">
                        <label className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name="tobaccoLicenseRevoked"
                            value="Yes"
                            checked={formData.tobaccoLicenseRevoked === 'Yes'}
                            onChange={handleInputChange}
                            className="text-[#840028] focus:ring-[#840028]"
                          />
                          <span className="text-gray-700 font-medium">Yes</span>
                        </label>
                        <label className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name="tobaccoLicenseRevoked"
                            value="No"
                            checked={formData.tobaccoLicenseRevoked === 'No'}
                            onChange={handleInputChange}
                            className="text-[#840028] focus:ring-[#840028]"
                          />
                          <span className="text-gray-700 font-medium">No</span>
                        </label>
                      </div>
                      {formData.tobaccoLicenseRevoked === 'Yes' && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">If yes, attach explanation.</label>
                          <textarea
                            name="tobaccoLicenseRevokedExplanation"
                            value={formData.tobaccoLicenseRevokedExplanation}
                            onChange={handleInputChange}
                            rows="2"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028]"
                            placeholder="Provide explanation..."
                          />
                        </div>
                      )}
                    </div>

                    <div className="bg-white p-4 rounded-lg border border-red-200">
                      <label className="block font-semibold text-gray-800 mb-3">
                        d. Have any tobacco tax liabilities that are past due in any jurisdiction?
                      </label>
                      <div className="flex space-x-6 mb-3">
                        <label className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name="tobaccoTaxLiabilities"
                            value="Yes"
                            checked={formData.tobaccoTaxLiabilities === 'Yes'}
                            onChange={handleInputChange}
                            className="text-[#840028] focus:ring-[#840028]"
                          />
                          <span className="text-gray-700 font-medium">Yes</span>
                        </label>
                        <label className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name="tobaccoTaxLiabilities"
                            value="No"
                            checked={formData.tobaccoTaxLiabilities === 'No'}
                            onChange={handleInputChange}
                            className="text-[#840028] focus:ring-[#840028]"
                          />
                          <span className="text-gray-700 font-medium">No</span>
                        </label>
                      </div>
                      {formData.tobaccoTaxLiabilities === 'Yes' && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">If yes, attach explanation.</label>
                          <textarea
                            name="tobaccoTaxLiabilitiesExplanation"
                            value={formData.tobaccoTaxLiabilitiesExplanation}
                            onChange={handleInputChange}
                            rows="2"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028]"
                            placeholder="Provide explanation..."
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t-2 border-red-300">
                    <h4 className="text-lg font-bold text-red-900 mb-4">If you are granted this license, do you agree to:</h4>
                    <div className="space-y-4">
                      <label className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-red-200">
                        <input
                          type="checkbox"
                          name="tobaccoAgreeWholesaler"
                          checked={formData.tobaccoAgreeWholesaler}
                          onChange={handleInputChange}
                          className="mt-1 text-[#840028] focus:ring-[#840028] rounded"
                        />
                        <span className="text-gray-800">
                          <strong>a.</strong> (If a wholesaler) Confirm that each entity to whom you sell and who is located on the Prairie Band Reservation is listed on the licensed Tribal Retailer list?
                        </span>
                      </label>

                      <label className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-red-200">
                        <input
                          type="checkbox"
                          name="tobaccoAgreePayment"
                          checked={formData.tobaccoAgreePayment}
                          onChange={handleInputChange}
                          className="mt-1 text-[#840028] focus:ring-[#840028] rounded"
                        />
                        <span className="text-gray-800">
                          <strong>b.</strong> Ensure that payment is remitted to the Tax Commission for each Tribal Tax Stamp affixed on individual packs of cigarettes by the tenth (10<sup>th</sup>) day of the month for the previous month?
                        </span>
                      </label>

                      <label className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-red-200">
                        <input
                          type="checkbox"
                          name="tobaccoAgreeReports"
                          checked={formData.tobaccoAgreeReports}
                          onChange={handleInputChange}
                          className="mt-1 text-[#840028] focus:ring-[#840028] rounded"
                        />
                        <span className="text-gray-800">
                          <strong>c.</strong> Submit all reports to the Tax Commission as required pursuant to the Potawatomi Law and Order Code Title 10 General Revenue and Taxation?
                        </span>
                      </label>

                      <label className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-red-200">
                        <input
                          type="checkbox"
                          name="tobaccoAgreeComply"
                          checked={formData.tobaccoAgreeComply}
                          onChange={handleInputChange}
                          className="mt-1 text-[#840028] focus:ring-[#840028] rounded"
                        />
                        <span className="text-gray-800">
                          <strong>d.</strong> Comply with all other applicable tribal business laws applicable to tribal tobacco retailers and wholesalers?
                        </span>
                      </label>

                      <label className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-red-200">
                        <input
                          type="checkbox"
                          name="tobaccoAgreeSecurity"
                          checked={formData.tobaccoAgreeSecurity}
                          onChange={handleInputChange}
                          className="mt-1 text-[#840028] focus:ring-[#840028] rounded"
                        />
                        <span className="text-gray-800">
                          <strong>e.</strong> Take all reasonable precautions to ensure the security and integrity of the Tribal Tax Stamp, including keeping unaffixed stamps in a secure location?
                        </span>
                      </label>

                      <label className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-red-200">
                        <input
                          type="checkbox"
                          name="tobaccoAgreeNonTransferable"
                          checked={formData.tobaccoAgreeNonTransferable}
                          onChange={handleInputChange}
                          className="mt-1 text-[#840028] focus:ring-[#840028] rounded"
                        />
                        <span className="text-gray-800">
                          <strong>f.</strong> Understand that this license, if granted, is non-transferable?
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8 mb-8 shadow-sm">
            <div className="flex items-center mb-6 pb-4 border-b border-gray-100">
              <div className="bg-[#840028] text-white p-2 rounded-lg mr-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800">Contact Information</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
              <div>
                <label className="block font-semibold text-gray-700 mb-3">Business Telephone <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  name="businessTelephone"
                  value={formData.businessTelephone}
                  onChange={handlePhoneNumberChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200"
                  placeholder="(555) 555-1234"
                  required
                />
              </div>
              <div>
                <label className="block font-semibold text-gray-700 mb-3">Home Telephone</label>
                <input
                  type="tel"
                  name="homeTelephone"
                  value={formData.homeTelephone}
                  onChange={handlePhoneNumberChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200"
                  placeholder="(555) 555-1234"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block font-semibold text-gray-700 mb-3">Contact Email <span className="text-red-500">*</span></label>
              <input
                type="email"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200"
                required
              />
            </div>

          <div className="w-full max-w-xs">
              <label className="block font-semibold text-gray-700 mb-3">Last 4 Digits of SSN/FEIN <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="ssnFein"
                value={formData.ssnFein}
                onChange={handleInputChange}
                className="w-full sm:w-32 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200 text-center"
                maxLength="4"
                required
              />
            </div>
          </div>

          {/* Business Details */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8 mb-8 shadow-sm">
            <div className="flex items-center mb-6 pb-4 border-b border-gray-100">
              <div className="bg-[#840028] text-white p-2 rounded-lg mr-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800">Business Details</h3>
            </div>

            <div className="space-y-8">
              {/* Nature of Business Field - Now Checkboxes */}
              <div>
                <label className="block font-semibold text-gray-700 mb-4">
                  Nature of Business <span className="text-red-500">*</span>
                  {formData.natureOfBusiness && formData.natureOfBusiness.length > 0 && (
                    <span className="text-sm text-green-600 font-normal ml-2">
                      ({formData.natureOfBusiness.length} selected)
                    </span>
                  )}
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {natureOfBusinessOptions.map(nature => (
                    <label key={nature} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer">
                      <input
                        type="checkbox"
                        name="natureOfBusiness"
                        value={nature}
                        checked={formData.natureOfBusiness?.includes(nature) || false}
                        onChange={handleInputChange}
                        className="text-[#840028] focus:ring-[#840028] rounded"
                      />
                      <span className="text-gray-700">{nature}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Food Vendor Training Notice - ALWAYS VISIBLE */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:space-x-3 space-y-3 sm:space-y-0">
                  <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h4 className="font-semibold text-yellow-800 mb-2">Food Vendor Training Requirement</h4>
                    <p className="text-yellow-700 text-sm mb-3">
                      <strong>Effective 7/1/2017:</strong> Persons that produce or sell food sold directly to the end consumer, must submit proof of training, within the past 2 years, in basic food handling principles that is accredited and certified by the American National Standards Institute (ANSI) or ServSafe.
                    </p>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
                      <a
                        href="https://www.ihs.gov/foodhandler"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium underline"
                      >
                        Online Course Link: <span className="break-all">https://www.ihs.gov/foodhandler</span>
                      </a>
                    </div>
                    <div className="mt-4 space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-4">
                      <div>
                        <label className="block font-semibold text-gray-700 mb-2">Food Handler Expiration Date</label>
                        <div className="w-full overflow-hidden rounded-lg border border-gray-300 sm:overflow-visible sm:rounded-none sm:border-0">
                          <input
                            type="date"
                            name="foodHandlerExpDate"
                            value={formData.foodHandlerExpDate}
                            onChange={handleInputChange}
                            className="ios-date-left w-full max-w-full min-w-0 box-border px-3 py-2 border-0 rounded-none text-left focus:outline-none focus:ring-2 focus:ring-[#840028] transition-all duration-150 sm:border sm:border-gray-300 sm:rounded-lg"
                            style={{ maxWidth: '100%' }}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block font-semibold text-gray-700 mb-2">Upload Food Handler Permit (PDF/Image) - Optional</label>
                        <input
                          type="file"
                          accept=".pdf,image/*"
                          onChange={(e) => handleFileChange(e, 'food')}
                          className="w-full"
                        />
                        {foodHandlerPermitFile && (
                          <p className="text-sm text-gray-600 mt-2">Selected: {foodHandlerPermitFile.name}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-3">Description of the Type of Business <span className="text-red-500">*</span></label>
                <textarea
                  name="businessDescription"
                  value={formData.businessDescription}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200 resize-vertical"
                  placeholder="Detailed description of business activities"
                  required
                />
              </div>

              {/* Liquor-specific fields */}
              {formData.typeOfProductService?.includes('Liquor') && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 space-y-6">
                  <h4 className="font-semibold text-purple-900 mb-4">Liquor License Information</h4>
                  
                  <div>
                    <label className="block font-semibold text-gray-700 mb-3">
                      Do you agree to comply with all of the terms and conditions of the Liquor Control Ordinance of the Prairie Band Potawatomi Nation and any other applicable law and do you agree to be subject to the exclusive jurisdiction of the Prairie Band Potawatomi Nation Tribal Court?
                    </label>
                    <div className="flex space-x-6">
                      <label className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="liquorCompliance"
                          value="Yes"
                          checked={formData.liquorCompliance === 'Yes'}
                          onChange={handleInputChange}
                          className="text-[#840028] focus:ring-[#840028]"
                        />
                        <span className="text-gray-700">Yes</span>
                      </label>
                      <label className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="liquorCompliance"
                          value="No"
                          checked={formData.liquorCompliance === 'No'}
                          onChange={handleInputChange}
                          className="text-[#840028] focus:ring-[#840028]"
                        />
                        <span className="text-gray-700">No</span>
                      </label>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-semibold text-gray-700 mb-3">ABC License #</label>
                      <input
                        type="text"
                        name="abcLicenseNumber"
                        value={formData.abcLicenseNumber}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200"
                        placeholder="Enter ABC License Number"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold text-gray-700 mb-3">Exp. Date</label>
                      <div className="w-full overflow-hidden rounded-lg border border-gray-300 sm:overflow-visible sm:rounded-none sm:border-0">
                        <input
                          type="date"
                          name="abcExpirationDate"
                          value={formData.abcExpirationDate}
                          onChange={handleInputChange}
                          className="ios-date-left w-full max-w-full min-w-0 box-border px-4 py-3 border-0 rounded-none text-left focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200 sm:border sm:border-gray-300 sm:rounded-lg"
                          style={{ maxWidth: '100%' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tobacco-specific fields */}
              {formData.typeOfProductService?.includes('Tobacco') && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 space-y-6">
                  <h4 className="font-semibold text-orange-900 mb-4">Tobacco Business Information</h4>
                  
                  <div>
                    <label className="block font-semibold text-gray-700 mb-3">
                      Date you began or will begin selling or importing tobacco or tobacco products on the Prairie Band Reservation:
                    </label>
                    <div className="w-full overflow-hidden rounded-lg border border-gray-300 sm:overflow-visible sm:rounded-none sm:border-0">
                      <input
                        type="date"
                        name="tobaccoStartDate"
                        value={formData.tobaccoStartDate}
                        onChange={handleInputChange}
                        className="ios-date-left w-full max-w-full min-w-0 box-border px-4 py-3 border-0 rounded-none text-left focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200 sm:border sm:border-gray-300 sm:rounded-lg"
                        style={{ maxWidth: '100%' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block font-semibold text-gray-700 mb-3">
                      Did you acquire an existing business?
                    </label>
                    <div className="flex space-x-6">
                      <label className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="acquiredExistingBusiness"
                          value="Yes"
                          checked={formData.acquiredExistingBusiness === 'Yes'}
                          onChange={handleInputChange}
                          className="text-[#840028] focus:ring-[#840028]"
                        />
                        <span className="text-gray-700">Yes</span>
                      </label>
                      <label className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="acquiredExistingBusiness"
                          value="No"
                          checked={formData.acquiredExistingBusiness === 'No'}
                          onChange={handleInputChange}
                          className="text-[#840028] focus:ring-[#840028]"
                        />
                        <span className="text-gray-700">No</span>
                      </label>
                    </div>
                  </div>

                  {formData.acquiredExistingBusiness === 'Yes' && (
                    <div>
                      <label className="block font-semibold text-gray-700 mb-3">If yes, list the previous business owner's name</label>
                      <input
                        type="text"
                        name="previousOwnerName"
                        value={formData.previousOwnerName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200"
                        placeholder="Enter previous owner's name"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Business Category Field */}
              <div>
                <label className="block font-semibold text-gray-700 mb-3">Business Category <span className="text-red-500">*</span></label>
                <div className="flex space-x-6">
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name="businessCategory"
                      value="Retailer"
                      checked={formData.businessCategory === 'Retailer'}
                      onChange={handleInputChange}
                      className="text-[#840028] focus:ring-[#840028]"
                      required
                    />
                    <span className="text-gray-700">Retailer</span>
                  </label>
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name="businessCategory"
                      value="Distributor"
                      checked={formData.businessCategory === 'Distributor'}
                      onChange={handleInputChange}
                      className="text-[#840028] focus:ring-[#840028]"
                      required
                    />
                    <span className="text-gray-700">Distributor</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block font-semibold text-gray-700 mb-3">Is this a new business?</label>
                  <div className="flex space-x-6">
                    <label className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="isNewBusiness"
                        value="Yes"
                        checked={formData.isNewBusiness === 'Yes'}
                        onChange={handleInputChange}
                        className="text-[#840028] focus:ring-[#840028]"
                      />
                      <span className="text-gray-700">Yes</span>
                    </label>
                    <label className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="isNewBusiness"
                        value="No"
                        checked={formData.isNewBusiness === 'No'}
                        onChange={handleInputChange}
                        className="text-[#840028] focus:ring-[#840028]"
                      />
                      <span className="text-gray-700">No</span>
                    </label>
                  </div>
                </div>

                {formData.isNewBusiness === 'Yes' && (
                  <div>
                    <label className="block font-semibold text-gray-700 mb-3">Business Start Date</label>
                    <div className="w-full overflow-hidden rounded-lg border border-gray-300 sm:overflow-visible sm:rounded-none sm:border-0">
                      <input
                        type="date"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleInputChange}
                        className="ios-date-left w-full max-w-full min-w-0 box-border px-4 py-3 border-0 rounded-none text-left focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200 sm:border sm:border-gray-300 sm:rounded-lg"
                        style={{ maxWidth: '100%' }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-3">Multiple Business Locations on Reservation?</label>
                <div className="flex space-x-6">
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name="multipleLocations"
                      value="Yes"
                      checked={formData.multipleLocations === 'Yes'}
                      onChange={handleInputChange}
                      className="text-[#840028] focus:ring-[#840028]"
                    />
                    <span className="text-gray-700">Yes</span>
                  </label>
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name="multipleLocations"
                      value="No"
                      checked={formData.multipleLocations === 'No'}
                      onChange={handleInputChange}
                      className="text-[#840028] focus:ring-[#840028]"
                    />
                    <span className="text-gray-700">No</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Certification */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8 mb-8 shadow-sm">
            <div className="flex items-center mb-6 pb-4 border-b border-gray-100">
              <div className="bg-[#840028] text-white p-2 rounded-lg mr-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800">Certification</h3>
            </div>

            <div className="space-y-6 text-gray-700">
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg">
                <div className="flex items-start space-x-4">
                  <input
                    type="checkbox"
                    name="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onChange={handleInputChange}
                    className="mt-1 text-[#840028] focus:ring-[#840028] rounded flex-shrink-0"
                    required
                  />
                  <p className="italic leading-relaxed">
                    I certify that I am a duly authorized owner, partner, director, officer, or representative of the applicant and that the statements made herein are true and correct to the best of my knowledge. I further agree on behalf of the applicant to be bound by and comply with all laws of the Prairie Band Potawatomi Nation and do hereby consensually submit to the jurisdiction of the Prairie Band Potawatomi Nation.
                  </p>
                </div>
              </div>

              {!formData.typeOfProductService?.includes('Fuel') && (
                <label className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer">
                  <input
                    type="checkbox"
                    name="agreeToReports"
                    checked={formData.agreeToReports}
                    onChange={handleInputChange}
                    className="mt-1 text-[#840028] focus:ring-[#840028] rounded"
                    required
                  />
                  <span className="font-medium">I agree to submit regular Sales Revenue reports, as requested by the PBPN Tax Commission Office.</span>
                </label>
              )}

              {formData.typeOfProductService?.includes('Fuel') && (
                <div className="space-y-4">
                  <label className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer">
                    <input
                      type="checkbox"
                      name="fuelAgreeOwnershipChange"
                      checked={formData.fuelAgreeOwnershipChange}
                      onChange={handleInputChange}
                      className="mt-1 text-[#840028] focus:ring-[#840028] rounded"
                      required
                    />
                    <span className="font-medium">I agree to report any change in ownership, including a change in partners, corporate officers or stockholders who own company stock within 30 days of the change.</span>
                  </label>

                  <label className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer">
                    <input
                      type="checkbox"
                      name="fuelAgreeMotorFuelReport"
                      checked={formData.fuelAgreeMotorFuelReport}
                      onChange={handleInputChange}
                      className="mt-1 text-[#840028] focus:ring-[#840028] rounded"
                      required
                    />
                    <span className="font-medium">
                      I agree to submit regular Motor Fuel Sales Revenue report by the 10<sup>th</sup> of the following calendar month. The incidence of the tax levied imposed by Section <strong>10-6-3 is declared and understood to be on the tribal distributor.</strong> Every tribal distributor is required to pay all applicable excise taxes at the applicable rate described in 10-6-3 to the Nation or its designee within (14) calendar days after the end of each calendar month. All records shall be kept by both the retailer and distributor for 3 years and readily available for PBPN Tax Commission inspection.
                    </span>
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Signature Section */}
          <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8 shadow-sm">
            <div className="flex items-center mb-6 pb-4 border-b border-gray-100">
              <div className="bg-[#840028] text-white p-2 rounded-lg mr-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800">Signature</h3>
            </div>

            <div className="mb-6">
              <label className="font-bold block mb-4 text-gray-700 text-lg">
                Sign below to certify the accuracy of this application:
              </label>
              <div
                id="signature-container"
                className="border-2 border-dashed border-gray-300 rounded-xl p-6 bg-white hover:border-gray-400 transition-colors duration-200"
              >
                {canvasSize.width > 0 && (
                  <SignatureCanvas
                    ref={sigCanvas}
                    penColor="#0000FF"
                    backgroundColor="rgb(255, 255, 255)"
                    canvasProps={{
                      width: canvasSize.width,
                      height: canvasSize.height,
                      className: 'signature-canvas w-full border border-gray-300 rounded-lg bg-white cursor-crosshair shadow-inner'
                    }}
                    onBegin={handleSignatureBegin}
                    onEnd={handleSignatureEnd}
                    clearOnResize={false}
                  />
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-2">
                {isSigned ? (
                  <>
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-green-600 font-semibold">Signature verified</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <span className="text-amber-600 font-medium">Signature required</span>
                  </>
                )}
              </div>

              <button
                type="button"
                onClick={clearSignature}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors duration-200 font-medium flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <span>Clear Signature</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Printed Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="printedName"
                  value={formData.printedName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200"
                  placeholder="Type your full name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Title <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#840028] focus:border-transparent transition-all duration-200"
                  placeholder="Your position/title"
                  required
                />
              </div>
            </div>

            <div className="border-t pt-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Date</label>
              <div className="w-full md:w-64 px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 font-medium text-gray-700">
                {currentDate}
              </div>
            </div>
          </div>

          {/* Fee Notice - Added above form actions */}
          {/* New License Fee Section */}
          <div className="mb-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-semibold text-blue-800 mb-2">New License Fee</p>
                <p className="text-blue-700 text-sm">
                  {(() => {
                    const feeMap = {
                      general: 50,
                      liquor: 530,
                      alcohol: 530,
                      fuel: 100,
                      tobacco: 100,
                      casino: 0,
                      caisno: 0
                    };
                    if (!formData.typeOfProductService || formData.typeOfProductService.length === 0) return 'Fee: $0';
                    const totalFee = formData.typeOfProductService.reduce((sum, type) => {
                      const key = type.toLowerCase();
                      return sum + (feeMap[key] !== undefined ? feeMap[key] : 0);
                    }, 0);
                    return `Total Fee: $${totalFee}`;
                  })()}
                </p>
              </div>
            </div>
          </div>
          {/* Fee Notice - Added above form actions */}
          <div className="mb-8 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-semibold text-yellow-800 mb-2">Important Payment Information</p>
                <p className="text-yellow-700 text-sm">
                  *Non-refundable Application Fee must be submitted with application along. Make checks payable to the Prairie Band Potawatomi Tax Commission.
                </p>
              </div>
            </div>
          </div>

          {/* Error Display for Empty Mandatory Fields */}
          {(() => {
            const emptyFields = [];
            
            // Check all mandatory fields
            if (!formData.businessName?.trim()) emptyFields.push('Business Name');
            if (!formData.businessAddress?.trim()) emptyFields.push('Business Address');
            if (!formData.ownerFirstName?.trim()) emptyFields.push('Owner First Name');
            if (!formData.ownerLastName?.trim()) emptyFields.push('Owner Last Name');
            if (!formData.ownerDateOfBirth?.trim()) emptyFields.push('Owner Date of Birth');
            if (!formData.ownerAddress?.trim()) emptyFields.push('Owner Address');
            if (!formData.ownerCity?.trim()) emptyFields.push('Owner City');
            if (!formData.ownerState?.trim()) emptyFields.push('Owner State');
            if (!formData.ownerZipCode?.trim()) emptyFields.push('Owner Zip Code');
            if (!formData.businessTelephone?.trim()) emptyFields.push('Business Telephone');
            if (!formData.contactEmail?.trim()) emptyFields.push('Contact Email');
            if (!formData.businessCategory?.trim()) emptyFields.push('Business Category');
            if (!formData.natureOfBusiness || formData.natureOfBusiness.length === 0) emptyFields.push('Nature of Business');
            if (!formData.typeOfProductService || formData.typeOfProductService.length === 0) emptyFields.push('Type of Product/Service');
            if (!formData.printedName?.trim()) emptyFields.push('Printed Name');
            if (!formData.title?.trim()) emptyFields.push('Title');
            if (!isSigned) emptyFields.push('Signature');
            if (!formData.agreeToTerms) emptyFields.push('Agreement to Terms');
            if (!formData.typeOfProductService?.includes('Fuel') && !formData.agreeToReports) emptyFields.push('Agreement to Reports');

            return emptyFields.length > 0 ? (
              <div className="mb-8 p-6 bg-red-50 border-2 border-red-300 rounded-lg">
                <div className="flex items-start space-x-3">
                  <svg className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="font-semibold text-red-800 mb-3">Please complete all mandatory fields:</p>
                    <ul className="text-red-700 text-sm space-y-2">
                      {emptyFields.map((field, index) => (
                        <li key={index} className="flex items-center space-x-2">
                          <span className="inline-block w-1.5 h-1.5 bg-red-600 rounded-full"></span>
                          <span>{field}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ) : null;
          })()}

          {/* Form Actions */}
          <div className="flex justify-center mt-10 mb-8">
            <button
              type="submit"
              disabled={!isSigned || !formData.agreeToTerms || (!formData.typeOfProductService?.includes('Fuel') && !formData.agreeToReports) || !formData.typeOfProductService || formData.typeOfProductService.length === 0 || !formData.businessCategory || !formData.natureOfBusiness || formData.natureOfBusiness.length === 0 || submitting || !formData.printedName.trim() || !formData.title.trim()}
              className="px-12 py-4 bg-[#840028] text-white font-semibold rounded-xl shadow-lg hover:bg-[#6a0020] focus:outline-none focus:ring-4 focus:ring-[#840028] focus:ring-opacity-50 transition-all duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none transform hover:scale-105 flex items-center space-x-3"
            >
              {submitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </span>
              ) : isSigned ? (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Submit Application</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  <span>Please Sign to Submit</span>
                </>
              )}
            </button>
          </div>
        </form>

        {/* Custom CSS for signature canvas */}
        <style jsx>{`
            .signature-canvas {
              cursor: crosshair !important;
              touch-action: none;
            }
            .signature-canvas:active {
              cursor: grabbing !important;
            }
            #signature-container {
              min-height: 150px;
            }
            select {
              background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
              background-position: right 0.5rem center;
              background-repeat: no-repeat;
              background-size: 1.5em 1.5em;
              padding-right: 2.5rem;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          `}</style>
      </div>
    </div>
  );
};

export default NewBusinessLicenseApplication;